<template lang="pug">
.waiters-table-component
  //- テーブル
  b-table(
    ref="waiterTable"
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- ヘッダー：要介護度・認知症生活自立度
    template(#head(nursingCareLevel)="data")
      .d-flex.flex-column
        span 要介護度
        span 認知症生活自立度
    //- ヘッダー：喀痰吸引・胃瘻
    template(#head(sputumSuction)="data")
      .d-flex.flex-column
        span 喀痰吸引
        span 胃瘻
    //- ヘッダー：申込者からの変更申請
    template(#head(changeRequest)="data")
      .d-flex.flex-column
        span 申込者からの
        span 変更申請
    //- ヘッダー：申込日・最終更新日
    template(#head(applicationDate)="data")
      .d-flex.flex-column
        span 申込日
        span 最終更新日
    //- ヘッダー:ID通知書
    template(#head(print)="data")
      .d-flex.flex-column
        span ID
        span 通知書

    //- セル：申請内容
    template(#cell(type)="row")
      //- 両方trueの場合に取り下げを優先する
      span(v-if="row.item.waiter.applyingForCancel == true") 取り下げ
      span(v-else-if="row.item.waiter.applyingForChange == true") 変更
    //- セル：届け出理由
    template(#cell(cancelReason)="row")
      //- 両方trueの場合に取り下げを優先する
      span(v-if="row.item.waiter.cancelReason") {{ row.item.waiter.cancelReason }}
    //- セル：対応
    template(#cell(applyingOperation)="row")
      //- 両方trueの場合に取り下げを優先する
      .d-flex.align-items-center.justify-content-center(v-if="row.item.waiter.applyingForCancel == true")
        button.btn.mr-2(@click="onApplyingForCancel(row.item, true)") 受理
        button.btn(@click="onApplyingForCancel(row.item, false)") 不受理
      .applying-change.d-flex.flex-column.align-items-center.justify-content-center(v-else-if="row.item.waiter.applyingForChange == true")
        button.btn.confirm-btn(@click="onConfirm(row.item)") 確認
        .confirm-btn-badge
          b-badge(pill variant="danger")
            .confirm-btn-badge-content
    //- セル：順位
    template(#cell(no)="row")
      span {{ row.item.no }}
    //- セル：ステップ
    template(#cell(step)="row")
      .d-flex.flex-column.align-items-center.justify-content-center
        span.mb-1 {{ convertStatusName(row.item.status) }}
        button.btn.edit-btn(@click="onStatusEdit(row.item)") 変更
    //- セル：入所申込者名
    template(#cell(name)="row")
      .d-flex.flex-row.flex-wrap.align-items-center
        span.mr-1 {{ row.item.name }}
        .waiter-label.mr-1(v-for="label in row.item.labels" :id="row.item.facilityId + '-' + row.item.residentsSortKey + '-label-' + label.id"
          :style="{ 'color': label.color, 'background-color': label.bgColor }") {{ label.name }}
          b-popover(:target="row.item.facilityId + '-' + row.item.residentsSortKey + '-label-' + label.id" triggers="hover") {{ label.description }}
        .mr-1
        .button
          .edit-label(@click.stop="onLabelEdit(row.item)" title="ラベルを編集します")
    //- セル：入所申込者名（ラベル無し）
    template(#cell(noLabelName)="row")
      .d-flex.flex-row.flex-wrap.align-items-center
        span.mr-1 {{ row.item.name }}
    //- セル：申込部屋
    template(#cell(rooms)="row")
      .d-flex.flex-row.flex-wrap
        span.room-name.p-1.mr-1(v-for="(room, index) in getRooms(row.item.waiter.admissionApplication.templateAnswer)"
          :class="room.className" :style="room.style") {{ room.name }}
    //- セル：点数
    template(#cell(point)="row")
      span {{ getPoint(row.item.waiter.admissionJudgement) }}
    //- セル：要介護度・認知症生活自立度
    template(#cell(nursingCareLevel)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isNursingCareLevelStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getNursingCareLevel(row.item.waiter.admissionApplication.templateAnswer) }}
        span(:class="{ 'strong': isDementiaDailyLifeIndependentLevelStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getDementiaDailyLifeIndependentLevel(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：要介護度
    template(#cell(careLevel)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isDementiaDailyLifeIndependentLevelStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getDementiaDailyLifeIndependentLevel(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：認知症生活自立度
    template(#cell(lifeLevel)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isNursingCareLevelStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getNursingCareLevel(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：喀痰吸引・胃瘻
    template(#cell(sputumSuction)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isSputumSuctionStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getSputumSuction(row.item.waiter.admissionApplication.templateAnswer) }}
        span(:class="{ 'strong': isGastrostomaStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getGastrostoma(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：喀痰吸引
    template(#cell(sputum)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isSputumSuctionStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getSputumSuction(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：胃瘻
    template(#cell(gastrostomy)="row")
      .d-flex.flex-column.align-items-center
        span(:class="{ 'strong': isGastrostomaStrong(row.item.waiter.admissionApplication.templateAnswer) }")
          | {{ getGastrostoma(row.item.waiter.admissionApplication.templateAnswer) }}
    //- セル：申込日・最終更新日
    template(#cell(applicationDate)="row")
      .d-flex.flex-column.align-items-center
        span {{ getApplicationDate(row.item.waiter.admissionApplication) }}
        span(:class="{ 'warning': isUpdatedWarning(row.item) }")
          | {{ getUpdated(row.item) }}
    //- セル：他施設の申込
    template(#cell(otherFacility)="row")
      .d-flex.flex-row.align-items-center.justify-content-end
        .appilication-count
          b-spinner.spinner.mr-1(v-if="row.item.otherFacilitiesAppilicationCount == undefined")
          span(v-else) {{ row.item.otherFacilitiesAppilicationCount }} 件
        .appilication-detail
          button.btn.detail-btn.disable(v-if="!row.item.otherFacilitiesAppilicationCount" disabled) 詳細
          button.btn.detail-btn(v-else @click="onOtherFacilitiesApplication(row.item)") 詳細
    //- セル：家族ユーザー
    template(#cell(family)="row")
      .d-flex.flex-row.align-items-center.justify-content-end
        .family-count
          span.mr-1 {{ row.item.familyCount }} 人
        .family-detail
          button.btn.detail-btn.disable(v-if="row.item.familyCount <= 0" disabled) 詳細
          button.btn.detail-btn(v-else @click="onShowFamilies(row.item)") 詳細
    //- セル:ID通知書
    template(#cell(print)="row")
      .d-flex.align-items-center.justify-content-center
        button.btn.print-btn(@click="onServiceRegisterGuide(row.item)") 印刷
    //- セル：登録情報
    template(#cell(operation)="row")
      button.btn.detail-btn.mr-2(@click="onDetails(row.item)") 詳細
      button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
    //- セル：登録情報(他施設)
    template(#cell(waiterViewableFacilitiesOperation)="row")
      button.btn.detail-btn(@click="onDetails(row.item)") 詳細
    //- セル：登録情報(一時保存)
    template(#cell(temporarilyOperation)="row")
      button.btn.detail-btn.mr-2(@click="onDetails(row.item)") 詳細
      button.btn.edit-btn.mr-2(@click="onWaiterEdit(row.item)") 修正
      button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";

import { ResidentStatus } from "@/commons/services/Residents";
import { AdmissionApplication, AdmissionJudgement, WaitersGetModel } from "@/commons/services/Waiters";
import {
  Template,
  TemplateItem,
  TemplateItemCheckbox,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemName,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSwitchCheckbox,
  TemplateType
} from "@/commons/services/AdmissionApplicationTemplates";
import {
  CalcType,
  TemplateItemText as JudgeTemplateItemText,
  TemplateItemRadio as JudgeTemplateItemRadio,
  TemplateItemMultipleCheckbox as JudgeTemplateItemMultipleCheckbox
} from "@/commons/services/AdmissionJudgementTemplates";
import { WaiterLabelsGetModel } from "@/commons/services/WaiterLabels";

/**
 * ### 待機者テーブルコンポーネント
 */
@Component({})
export default class WaiterTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<WaiterTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<WaiterTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<WaiterTableItem> {
    return this.value;
  }

  /**
   * ### 状態と状態名を変換する
   */
  public convertStatusName(status: ResidentStatus): string {
    let statusName: string = "";

    switch (status) {
      case ResidentStatus.REGISTERD:
        statusName = "登録者";
        break;
      case ResidentStatus.CANCELED:
        statusName = "取り下げ";
        break;
      case ResidentStatus.TEMPORARILY:
        statusName = "一時保存";
        break;
      case ResidentStatus.APPLICATION:
        statusName = "入所申込み";
        break;
      case ResidentStatus.RECEIVED_APPLICATION:
        statusName = "申込み受理";
        break;
      case ResidentStatus.COMPLETE_APPLICATION:
        statusName = "判定済";
        break;
      case ResidentStatus.CONFIRM_ADMISSION:
        statusName = "入所意向確認";
        break;
      case ResidentStatus.FINAL_JUDGE:
        statusName = "最終判定中";
        break;
      case ResidentStatus.COMPLETE_FINAL_JUDGE:
        statusName = "最終判定済";
        break;
      case ResidentStatus.ADMISSION_PROCEDURE:
        statusName = "入所手続き";
        break;
      case ResidentStatus.RESIDENT:
        statusName = "利用者";
        break;
      default:
        statusName = "";
        break;
    }

    return statusName;
  }

  /**
   * ### ラベルの編集ボタン押下イベント
   */
  public onLabelEdit(item: WaiterTableItem): void {
    this.$emit("onLabelEdit", item);
    return;
  }

  /**
   * ### 部屋種類名を返す
   */
  public getRooms(templateAnswer: Template): Array<{ name: string; className: string; style: string }> {
    let rooms: Array<{ name: string; className: string; style: string }> = [];
    if (!templateAnswer || !templateAnswer.properties) {
      return rooms;
    }
    try {
      const item: TemplateItemRoomType | null = this.serachTemplateProperty(templateAnswer.properties, "roomType") as TemplateItemRoomType;

      if (item == null) {
        return rooms;
      }

      for (const answer of item.answer) {
        if (userStore.facility.id == answer.facilityId) {
          // 共通
          if (answer.recruitmentRoom.multiBed == true) {
            rooms.push({
              name: "従多",
              className: "multi-bed",
              style: "margin-top:2px; margin-bottom:2px;"
            });
          }
          if (answer.recruitmentRoom.conventinalSingle == true) {
            rooms.push({
              name: "従個",
              className: "conventinal-single",
              style: "margin-top:2px; margin-bottom:2px;"
            });
          }
          if (answer.recruitmentRoom.unitTypeSingle == true) {
            rooms.push({
              name: "ユ個",
              className: "unit-type-single",
              style: "margin-top:2px; margin-bottom:2px;"
            });
          }
          if (answer.recruitmentRoom.single == true) {
            rooms.push({
              name: "個室",
              className: "single",
              style: "margin-top:2px; margin-bottom:2px;"
            });
          }
          if (answer.recruitmentRoom.double == true) {
            rooms.push({
              name: "２人",
              className: "double",
              style: "margin-top:2px; margin-bottom:2px;"
            });
          }

          // 施設固有
          if (answer.customRecruitmentRoom && 0 < answer.customRecruitmentRoom.length) {
            for (const room of answer.customRecruitmentRoom) {
              if (room.use == true) {
                rooms.push({
                  name: room.abbreviationName,
                  className: "",
                  style: `margin-top:2px; margin-bottom:2px; background: ${room.color};`
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error(templateAnswer);
    }

    return rooms;
  }

  /**
   * ### テンプレートのpropertiesから指定されたidの要素を返す
   */
  public serachTemplateProperty(properties: Array<TemplateItem>, idName: string): TemplateItemInput | null {
    for (const property of properties) {
      // 配下にperopetiesがあれば再帰検索
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const section: TemplateItemSection = property as TemplateItemSection;
          const item: TemplateItemInput | null = this.serachTemplateProperty(section.properties, idName);
          if (item != null) {
            return item;
          }
        } else if (property.type == TemplateType.GROUP) {
          const group: TemplateItemGroup = property as TemplateItemGroup;
          if (group.properties != null) {
            for (const childProperties of group.properties) {
              const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties, idName);
              if (item != null) {
                return item;
              }
            }
          } else {
            return null;
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const radioGroup: TemplateItemRadioGroup = property as TemplateItemRadioGroup;
          if (radioGroup.properties != null) {
            for (const childProperties of radioGroup.properties) {
              if (childProperties.properties != null) {
                const item: TemplateItemInput | null = this.serachTemplateProperty(childProperties.properties, idName);
                if (item != null) {
                  return item;
                }
              }
            }
          } else {
            return null;
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const switchCheckbox: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;
          if (switchCheckbox.properties != null) {
            const item: TemplateItemInput | null = this.serachTemplateProperty(switchCheckbox.properties, idName);
            if (item != null) {
              return item;
            }
          } else {
            return null;
          }
        }
      }

      if (property.id == idName) {
        return property as TemplateItemInput;
      }
    }
    return null;
  }

  /**
   * ### propertiesを持つ要素か否か
   */
  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 入居判定結果の点数を返す
   */
  public getPoint(admissionJudgement: AdmissionJudgement): string {
    if (admissionJudgement == null || admissionJudgement.templateAnswer == null) {
      return "";
    }

    let totalPoint: number = 0;
    let replacePoint: number | null = null;
    let points: { totalPoint: number; replacePoint: number | null } = { totalPoint, replacePoint };
    this.calculateReplacePoint(admissionJudgement.templateAnswer.properties, points);
    this.calculateTotalPoint(admissionJudgement.templateAnswer.properties, points);

    if (points.replacePoint != null) {
      return `${points.replacePoint}`;
    } else {
      return `${points.totalPoint}`;
    }
  }

  /**
   * ### 入居判定点数計算(点数置き換え)
   */
  public calculateReplacePoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateReplacePoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateReplacePoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: JudgeTemplateItemText = property as JudgeTemplateItemText;
          if (textItem.answer == null) {
            continue;
          }

          if (textItem.calcType == CalcType.REPLACE) {
            points.replacePoint = Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: JudgeTemplateItemRadio = property as JudgeTemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }

          if (radioItem.calcType == CalcType.REPLACE) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.replacePoint = option.point ? Number(option.point) : (option.point as null);
                break;
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: JudgeTemplateItemMultipleCheckbox = property as JudgeTemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.REPLACE) {
            let point: number | null = null;
            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }
                if (point == null || point < Number(multipleCheckboxItem.points[i])) {
                  point = Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            points.replacePoint = point != null ? point : null;
          }
        }
      }
    }
    return;
  }

  /**
   * ### 入居判定点数計算(加点)
   */
  public calculateTotalPoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateTotalPoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateTotalPoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: JudgeTemplateItemText = property as JudgeTemplateItemText;
          if (textItem.answer == null) {
            continue;
          }

          if (textItem.calcType == CalcType.ADD || textItem.calcType == CalcType.CALC_ADD) {
            points.totalPoint += Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: JudgeTemplateItemRadio = property as JudgeTemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }

          if (radioItem.calcType == CalcType.ADD || radioItem.calcType == CalcType.CALC_ADD) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.totalPoint += Number(option.point);
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: JudgeTemplateItemMultipleCheckbox = property as JudgeTemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.ADD || multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
            let point: number = 0;

            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }

                // 計算種別：加算の場合は選択肢で最高得点を採用する
                if (multipleCheckboxItem.calcType == CalcType.ADD) {
                  if (point < Number(multipleCheckboxItem.points[i])) {
                    point = Number(multipleCheckboxItem.points[i]);
                  }
                }
                // 計算種別：計算加算の場合は選択されたpointを合算する
                else if (multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
                  point += Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            // 計算種別：計算加算の場合は上限までの点数とする
            if (multipleCheckboxItem.calcType == CalcType.CALC_ADD && multipleCheckboxItem.limitPoint != null && multipleCheckboxItem.limitPoint != undefined) {
              if (multipleCheckboxItem.limitPoint < point) {
                point = multipleCheckboxItem.limitPoint;
              }
            }

            points.totalPoint += point;
          }
        }
      }
    }
    return;
  }

  /**
   * ### 要介護度を強調するか否か
   */
  public isNursingCareLevelStrong(templateAnswer: Template): boolean {
    if (!templateAnswer || !templateAnswer.properties) {
      return false;
    }
    const item: TemplateItemRadio | null = this.serachTemplateProperty(templateAnswer.properties, "careLevel") as TemplateItemRadio;

    if (item == null) {
      return false;
    }

    if (304 <= Number(item.answer)) {
      return true;
    }

    return false;
  }

  /**
   * ### 要介護度を返す
   */
  public getNursingCareLevel(templateAnswer: Template): string {
    if (!templateAnswer || !templateAnswer.properties) {
      return "";
    }
    const item: TemplateItemRadio | null = this.serachTemplateProperty(templateAnswer.properties, "careLevel") as TemplateItemRadio;

    if (item == null) {
      return "";
    }

    for (const option of item.options) {
      if (option.value == item.answer) {
        return option.text;
      }
    }

    return "";
  }

  /**
   * ### 認知症生活自立度を強調するか否か
   */
  public isDementiaDailyLifeIndependentLevelStrong(templateAnswer: Template): boolean {
    if (!templateAnswer || !templateAnswer.properties) {
      return false;
    }
    const item: TemplateItemRadio | null = this.serachTemplateProperty(templateAnswer.properties, "dementiaDailyLifeIndependentLevel") as TemplateItemRadio;

    if (item == null) {
      return false;
    }

    if (131 <= Number(item.answer)) {
      return true;
    }

    return false;
  }

  /**
   * ### 認知症生活自立度を返す
   */
  public getDementiaDailyLifeIndependentLevel(templateAnswer: Template): string {
    if (!templateAnswer || !templateAnswer.properties) {
      return "";
    }
    const item: TemplateItemRadio | null = this.serachTemplateProperty(templateAnswer.properties, "dementiaDailyLifeIndependentLevel") as TemplateItemRadio;

    if (item == null) {
      return "";
    }

    for (const option of item.options) {
      if (option.value == item.answer) {
        return option.text;
      }
    }

    return "";
  }

  /**
   * ### 喀痰吸引を強調するか否か
   */
  public isSputumSuctionStrong(templateAnswer: Template): boolean {
    if (!templateAnswer || !templateAnswer.properties) {
      return false;
    }
    const item: TemplateItem | null = this.serachTemplateProperty(templateAnswer.properties, "sputumSuction");

    if (item == null) {
      return false;
    }

    // radioとcheckboxのパターンがあるので分岐して情報取得する
    switch (item.type) {
      case TemplateType.RADIO:
      case TemplateType.RADIO_GROUP: {
        const _item: TemplateItemRadio = item as TemplateItemRadio;
        return _item.answer == "有り" ? true : false;
      }
      case TemplateType.CHECKBOX:
      case TemplateType.SWITCH_CHECKBOX: {
        const _item: TemplateItemCheckbox = item as TemplateItemCheckbox;
        return _item.answer == true ? true : false;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 喀痰吸引を返す
   */
  public getSputumSuction(templateAnswer: Template): string {
    if (!templateAnswer || !templateAnswer.properties) {
      return "";
    }
    const item: TemplateItem | null = this.serachTemplateProperty(templateAnswer.properties, "sputumSuction");

    if (item == null) {
      return "";
    }

    // radioとcheckboxのパターンがあるので分岐して情報取得する
    switch (item.type) {
      case TemplateType.RADIO: {
        const _item: TemplateItemRadio = item as TemplateItemRadio;
        for (const option of _item.options) {
          if (option.value == _item.answer) {
            return option.text;
          }
        }
        return "";
      }
      case TemplateType.RADIO_GROUP: {
        const _item: TemplateItemRadioGroup = item as TemplateItemRadioGroup;
        for (const property of _item.properties) {
          if (property.option.value == _item.answer) {
            return property.option.text;
          }
        }
        return "";
      }
      case TemplateType.CHECKBOX:
      case TemplateType.SWITCH_CHECKBOX: {
        const _item: TemplateItemCheckbox = item as TemplateItemCheckbox;
        return _item.answer == true ? "有り" : "無し";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 胃漏を強調するか否か
   */
  public isGastrostomaStrong(templateAnswer: Template): boolean {
    if (!templateAnswer || !templateAnswer.properties) {
      return false;
    }
    const item: TemplateItem | null = this.serachTemplateProperty(templateAnswer.properties, "gastrostoma");

    if (item == null) {
      return false;
    }

    // radioとcheckboxのパターンがあるので分岐して情報取得する
    switch (item.type) {
      case TemplateType.RADIO:
      case TemplateType.RADIO_GROUP: {
        const _item: TemplateItemRadio = item as TemplateItemRadio;
        return _item.answer == "有り" ? true : false;
      }
      case TemplateType.CHECKBOX:
      case TemplateType.SWITCH_CHECKBOX: {
        const _item: TemplateItemCheckbox = item as TemplateItemCheckbox;
        return _item.answer == true ? true : false;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### 胃漏を返す
   */
  public getGastrostoma(templateAnswer: Template): string {
    if (!templateAnswer || !templateAnswer.properties) {
      return "";
    }
    const item: TemplateItem | null = this.serachTemplateProperty(templateAnswer.properties, "gastrostoma");

    if (item == null) {
      return "";
    }

    // radioとcheckboxのパターンがあるので分岐して情報取得する
    switch (item.type) {
      case TemplateType.RADIO: {
        const _item: TemplateItemRadio = item as TemplateItemRadio;
        for (const option of _item.options) {
          if (option.value == _item.answer) {
            return option.text;
          }
        }
        return "";
      }
      case TemplateType.RADIO_GROUP: {
        const _item: TemplateItemRadioGroup = item as TemplateItemRadioGroup;
        for (const property of _item.properties) {
          if (property.option.value == _item.answer) {
            return property.option.text;
          }
        }
        return "";
      }
      case TemplateType.CHECKBOX:
      case TemplateType.SWITCH_CHECKBOX: {
        const _item: TemplateItemCheckbox = item as TemplateItemCheckbox;
        return _item.answer == true ? "有り" : "無し";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 申込日を返す
   */
  public getApplicationDate(admissionApplication: AdmissionApplication): string {
    if (admissionApplication == null) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(admissionApplication.created, "yyyy年mm月dd日");
  }

  /**
   * ### 最終更新日を強調するか否か
   */
  public isUpdatedWarning(item: WaitersGetModel): boolean {
    const warningLine: Date = new Date();
    warningLine.setMonth(warningLine.getMonth() - 6);

    if (item.updated <= this.$dateUtil.convertDateToNumber(warningLine, "yyyymmddhhMMss000")) {
      return true;
    }

    return false;
  }

  /**
   * ### 最終更新日を返す
   */
  public getUpdated(item: WaitersGetModel): string {
    return this.$dateUtil.convertNumberToString(item.updated, "yyyy年mm月dd日");
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: WaiterTableItem, filter: FilterSetting): boolean {
    return (
      this.filterSearchWords(row, filter) &&
      this.filterStatus(row, filter) &&
      this.filterGender(row, filter) &&
      this.filterRoomType(row, filter) &&
      this.filterCareLevel(row, filter) &&
      this.filterLabel(row, filter)
    );
  }

  /**
   * ### 検索ワードによるフィルタリング
   *
   * - 検索ワードが空の場合はフィルタリング対象外
   */
  public filterSearchWords(row: WaiterTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundName: boolean = false;

    for (const word of words) {
      // 申込者名検索
      if (row.name && row.name.indexOf(word) != -1) {
        foundName = true;
        break;
      }
      // 申込者名(かな)検索
      if (row.waiter && row.waiter.admissionApplication && row.waiter.admissionApplication.templateAnswer) {
        const item: TemplateItemName | null = this.serachTemplateProperty(row.waiter.admissionApplication.templateAnswer.properties, "name") as TemplateItemName;
        if (item && item.answer) {
          if ((item.answer.firstKanaName && item.answer.firstKanaName.indexOf(word) != -1) || (item.answer.secondKanaName && item.answer.secondKanaName.indexOf(word) != -1)) {
            foundName = true;
            break;
          }
        }
      }
    }

    return foundName;
  }

  /**
   * ### 利用者状態によるフィルタリング
   *
   * - 「全て」を選択した場合はフィルタリング対象外
   */
  public filterStatus(row: WaiterTableItem, filter: FilterSetting): boolean {
    if (
      filter.status.application == false &&
      filter.status.receivedApplication == false &&
      filter.status.completeApplication == false &&
      filter.status.confirmAdmission == false &&
      filter.status.finalJudge == false &&
      filter.status.completeFinalJudge == false &&
      filter.status.admissionProcedure == false
    ) {
      return true;
    }

    if (filter.status.application == true) {
      if (row.status == ResidentStatus.APPLICATION) {
        return true;
      }
    }
    if (filter.status.receivedApplication == true) {
      if (row.status == ResidentStatus.RECEIVED_APPLICATION) {
        return true;
      }
    }
    if (filter.status.completeApplication == true) {
      if (row.status == ResidentStatus.COMPLETE_APPLICATION) {
        return true;
      }
    }
    if (filter.status.confirmAdmission == true) {
      if (row.status == ResidentStatus.CONFIRM_ADMISSION) {
        return true;
      }
    }
    if (filter.status.finalJudge == true) {
      if (row.status == ResidentStatus.FINAL_JUDGE) {
        return true;
      }
    }
    if (filter.status.completeFinalJudge == true) {
      if (row.status == ResidentStatus.COMPLETE_FINAL_JUDGE) {
        return true;
      }
    }
    if (filter.status.admissionProcedure == true) {
      if (row.status == ResidentStatus.ADMISSION_PROCEDURE) {
        return true;
      }
    }

    return false;
  }

  /**
   * ### 性別によるフィルタリング
   *
   * - 男性、女性が選択されていない場合はフィルタリング対象外
   */
  public filterGender(row: WaiterTableItem, filter: FilterSetting): boolean {
    if (filter.gender.men == false && filter.gender.women == false) {
      return true;
    }

    if (filter.gender.men == true) {
      if (row.gender == "男性") {
        return true;
      }
    }
    if (filter.gender.women == true) {
      if (row.gender == "女性") {
        return true;
      }
    }

    return false;
  }

  /**
   * ### 部屋種類によるフィルタリング
   *
   * - 部屋種類(施設固有も含む)が選択されていない場合はフィルタリング対象外
   * - 待機者の部屋種類が存在しない場合はフィルタリング対象外
   */
  public filterRoomType(row: WaiterTableItem, filter: FilterSetting): boolean {
    let customRoomTypeChecked: boolean = false;
    for (const roomType of filter.customRoomType) {
      if (roomType.value == true) {
        customRoomTypeChecked = true;
        break;
      }
    }
    if (
      filter.roomType.multiBed == false &&
      filter.roomType.conventinalSingle == false &&
      filter.roomType.unitTypeSingle == false &&
      filter.roomType.single == false &&
      filter.roomType.double == false &&
      customRoomTypeChecked == false
    ) {
      return true;
    }

    if (!row.waiter || !row.waiter.admissionApplication || !row.waiter.admissionApplication.templateAnswer) {
      return false;
    }

    const item: TemplateItemRoomType | null = this.serachTemplateProperty(row.waiter.admissionApplication.templateAnswer.properties, "roomType") as TemplateItemRoomType;
    if (item == null) {
      return true;
    }

    for (const answer of item.answer) {
      if (userStore.facility.id == answer.facilityId) {
        // 施設共通の部屋種類
        if (filter.roomType.multiBed == true) {
          if (answer.recruitmentRoom.multiBed == true) {
            return true;
          }
        }
        if (filter.roomType.conventinalSingle == true) {
          if (answer.recruitmentRoom.conventinalSingle == true) {
            return true;
          }
        }
        if (filter.roomType.unitTypeSingle == true) {
          if (answer.recruitmentRoom.unitTypeSingle == true) {
            return true;
          }
        }
        if (filter.roomType.single == true) {
          if (answer.recruitmentRoom.single == true) {
            return true;
          }
        }
        if (filter.roomType.double == true) {
          if (answer.recruitmentRoom.double == true) {
            return true;
          }
        }

        // 施設固有の部屋種類
        for (const customRoomType of filter.customRoomType) {
          if (customRoomType.value == true) {
            // 既存待機者等、customRecruitmentRoomが存在しない場合はスキップする
            if (!answer.customRecruitmentRoom) {
              continue;
            }
            for (const customRoom of answer.customRecruitmentRoom) {
              if (customRoomType.id == customRoom.id && customRoom.use == true) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * ### 要介護度によるフィルタリング
   *
   * - 要介護度が選択されていない場合はフィルタリング対象外
   * - 待機者の要介護度が存在しない場合はフィルタリング対象外
   */
  public filterCareLevel(row: WaiterTableItem, filter: FilterSetting): boolean {
    if (
      filter.careLevel.careLevel1000 == false &&
      filter.careLevel.careLevel1010 == false &&
      filter.careLevel.careLevel2010 == false &&
      filter.careLevel.careLevel2020 == false &&
      filter.careLevel.careLevel3010 == false &&
      filter.careLevel.careLevel3020 == false &&
      filter.careLevel.careLevel3030 == false &&
      filter.careLevel.careLevel3040 == false &&
      filter.careLevel.careLevel3050 == false
    ) {
      return true;
    }

    if (!row.waiter || !row.waiter.admissionApplication || !row.waiter.admissionApplication.templateAnswer) {
      return false;
    }

    const item: TemplateItemRadio | null = this.serachTemplateProperty(row.waiter.admissionApplication.templateAnswer.properties, "careLevel") as TemplateItemRadio;
    if (item == null) {
      return true;
    }

    if (filter.careLevel.careLevel1000 == true) {
      if (item.answer == "1000") {
        return true;
      }
    }
    if (filter.careLevel.careLevel1010 == true) {
      if (item.answer == "1010") {
        return true;
      }
    }
    if (filter.careLevel.careLevel2010 == true) {
      if (item.answer == "2010") {
        return true;
      }
    }
    if (filter.careLevel.careLevel2020 == true) {
      if (item.answer == "2020") {
        return true;
      }
    }
    if (filter.careLevel.careLevel3010 == true) {
      if (item.answer == "3010") {
        return true;
      }
    }
    if (filter.careLevel.careLevel3020 == true) {
      if (item.answer == "3020") {
        return true;
      }
    }
    if (filter.careLevel.careLevel3030 == true) {
      if (item.answer == "3030") {
        return true;
      }
    }
    if (filter.careLevel.careLevel3040 == true) {
      if (item.answer == "3040") {
        return true;
      }
    }
    if (filter.careLevel.careLevel3050 == true) {
      if (item.answer == "3050") {
        return true;
      }
    }

    return false;
  }

  /**
   * ### ラベルによるフィルタリング
   *
   * - 全てが選択されていない場合はフィルタリング対象外
   */
  public filterLabel(row: WaiterTableItem, filter: FilterSetting): boolean {
    let waiterlabelMap: Map<number, WaiterLabelsGetModel> = new Map<number, WaiterLabelsGetModel>();
    for (const label of row.labels) {
      waiterlabelMap.set(label.id, label);
    }

    let existChecked: boolean = false;
    for (const label of filter.labels) {
      if (label.checked == true) {
        existChecked = true;

        if (waiterlabelMap.get(label.id)) {
          return true;
        }
      }
    }
    if (existChecked == false) {
      return true;
    }

    return false;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 変更ボタン押下イベント
   */
  public onStatusEdit(item: WaiterTableItem): void {
    this.$emit("onStatusEdit", item);
    return;
  }

  /**
   * ### 他施設の申込ボタン押下イベント
   */
  public onOtherFacilitiesApplication(item: WaiterTableItem): void {
    this.$emit("onOtherFacilitiesApplication", item);
    return;
  }

  /**
   * ### 家族ユーザー詳細ボタン押下イベント
   */
  public onShowFamilies(item: WaiterTableItem): void {
    this.$emit("onShowFamilies", item);
    return;
  }

  /**
   * ### ID通知書ボタン押下イベント
   */
  public onServiceRegisterGuide(item: WaiterTableItem): void {
    this.$emit("onServiceRegisterGuide", item);
    return;
  }

  /**
   * ### 確認ボタン押下イベント
   */
  public onConfirm(item: WaiterTableItem): void {
    this.$emit("onConfirm", item);
    return;
  }

  /**
   * ### 受理または不受理ボタン押下イベント
   */
  public onApplyingForCancel(item: WaiterTableItem, answer: boolean): void {
    this.$emit("onApplyingForCancel", item, answer);
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetails(item: WaiterTableItem): void {
    this.$emit("onDetails", item);
    return;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onWaiterEdit(item: WaiterTableItem): void {
    this.$emit("onWaiterEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: WaiterTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface WaiterTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface WaiterTableItem extends WaitersGetModel {
  /** 順位 */
  no: number;
  /** 他施設の申込件数 */
  otherFacilitiesAppilicationCount?: number;
  /** 他施設の申込情報 */
  otherFacilitiesAppilicationWaiters?: Array<WaitersGetModel>;
  /** ラベル */
  labels: Array<WaiterLabelsGetModel>;
  /** 家族数 */
  familyCount: number;
}

export interface FilterSetting {
  /** 文字列検索 */
  searchWord: string;
  /** 利用者状態 */
  status: {
    /** 入居申込み */
    application: boolean;
    /** 申込み受理 */
    receivedApplication: boolean;
    /** 判定済 */
    completeApplication: boolean;
    /** 入居意向確認 */
    confirmAdmission: boolean;
    /** 最終判定中 */
    finalJudge: boolean;
    /** 最終判定済み */
    completeFinalJudge: boolean;
    /** 入居手続き */
    admissionProcedure: boolean;
  };
  /** 性別 */
  gender: {
    /** 男性 */
    men: boolean;
    /** 女性 */
    women: boolean;
  };
  /** 部屋種類 */
  roomType: {
    /** 従来型多床室 */
    multiBed: boolean;
    /** 従来型個室 */
    conventinalSingle: boolean;
    /** ユニット型個室 */
    unitTypeSingle: boolean;
    /** 個室 */
    single: boolean;
    /** ２人部屋（夫婦部屋） */
    double: boolean;
  };
  /** 部屋種類(固有) */
  customRoomType: Array<FilterCustomRoomType>;
  /** 要介護度 */
  careLevel: {
    /** 自立 */
    careLevel1000: boolean;
    /** 申請中 */
    careLevel1010: boolean;
    /** 要支援１ */
    careLevel2010: boolean;
    /** 要支援２ */
    careLevel2020: boolean;
    /** 要介護1 */
    careLevel3010: boolean;
    /** 要介護2 */
    careLevel3020: boolean;
    /** 要介護3 */
    careLevel3030: boolean;
    /** 要介護4 */
    careLevel3040: boolean;
    /** 要介護5 */
    careLevel3050: boolean;
  };
  /** ラベル */
  labels: Array<FilterLabel>;
}
export interface FilterLabel extends WaiterLabelsGetModel {
  checked: boolean;
}
export interface FilterCustomRoomType {
  /** 識別ID */
  id: string;
  /** 名称 */
  name: string;
  /** チェック */
  value: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

.waiters-table-component {
  .edit-btn,
  .other-facility-btn,
  .print-btn,
  .confirm-btn,
  .detail-btn,
  .delete-btn {
    width: 64px;
  }
  .confirm-btn-badge {
    position: absolute;
    top: -8px;
    left: 94px;
    z-index: 1;
    .confirm-btn-badge-content {
      width: 5px;
      height: 12px;
    }
  }

  .waiter-label {
    margin: 2px 0;
    font-size: 12px;
    padding: 4px;
    border-radius: 0.25rem;
  }
  .edit-label {
    @include svg-icon("../../../assets/pen.svg", $font-base-color, 20px, 20px);
    opacity: 0.5;
    text-shadow: 0 1px 0 #fff;
    cursor: pointer;
  }
  .edit-label:hover {
    opacity: 0.75;
  }

  .room-name {
    width: 34px;
    min-width: 34px;
    font-size: 12px;
    border: solid 1px $border-darkgray;
  }
  .multi-bed {
    background: #ecd8be;
  }
  .conventinal-single,
  .unit-type-single,
  .single {
    background: #e8f3f5;
  }
  .double {
    background: #93bafa;
  }

  .strong {
    color: $font-key-color;
    font-weight: bold;
  }
  .warning {
    color: $font-strong-color;
    font-weight: bold;
  }
}
</style>
